import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import AppleLogo from "@/components/icons/AppleLogo";
import { MACOS_DOWNLOAD_URL, trackDownloadClick } from "@/lib/desktopDownload";
import WindowsWaitlistButton from "@/components/desktop/WindowsWaitlistButton";
import MacLinkEmailButton from "@/components/desktop/MacLinkEmailButton";
import MacLinkTextButton, { IMESSAGE_HANDLE } from "@/components/desktop/MacLinkTextButton";
import { detectPlatform, type VisitorPlatform } from "@/lib/platform";
import { trackEvent } from "@/utils/mixpanel";

// The adaptive desktop-app CTA — one component, four audiences:
//
//   mac      → "Download for macOS" (the real dmg, with click feedback + a
//              redirect to /desktop/installing so the next steps are on screen
//              while the download runs — no more invisible downloads and
//              rage-clicks)
//   windows  → the Windows waitlist, full-strength (not the dimmed secondary)
//   ios      → "Text me the Mac link" when VITE_IMESSAGE_HANDLE is set (they
//              text us, we reply with the link), email as the alt link
//   ios/android/linux/other
//            → "Email me the Mac link" (a phone cannot install a dmg; capture
//              the intent and put the personalized link in their inbox)
//
// WHY: the Aug 2026 funnel audit. 71% of download clicks came from devices
// that cannot run the app; Mac users clicked 3.8x each because nothing
// acknowledged the click; mobile users misused the Windows waitlist as a
// generic "notify me". Every branch here ends in something that can succeed.
//
// Detection happens in an effect, not at render: the page is prerendered, and
// the server doesn't know the device. Defaulting to the mac variant keeps the
// prerendered HTML byte-identical to the old button (a working <a href> even
// before hydration), then the effect swaps the variant on mount.

interface DownloadCtaProps {
  /** Which surface this CTA sits on ("hero", "pricing", "footer", "navbar"). */
  placement: string;
  /** Primary button classes — shared across variants so each page's design
   *  carries over regardless of which branch renders. */
  className?: string;
  iconClassName?: string;
  /** Extra inline style for the primary button (e.g. --gamey-shadow vars). */
  style?: React.CSSProperties;
  /** Render the small alternative link under the primary button (hero yes,
   *  navbar/pricing-card no — no room). */
  showAltLink?: boolean;
  /** Classes for the alt-link row (color depends on the background). */
  altLinkClassName?: string;
  /** Skip the /desktop/installing redirect after a mac download click. */
  noInstallingRedirect?: boolean;
}

const DEFAULT_CLASSES =
  "btn-gamey inline-flex items-center gap-2.5 bg-white hover:bg-slate-50 text-[#0B6BB0] font-bold uppercase text-sm xl:text-base tracking-wide px-8 py-4 xl:px-9 xl:py-[1.15rem] rounded-2xl transition-colors";

export default function DownloadCta({
  placement,
  className = DEFAULT_CLASSES,
  iconClassName = "w-4 h-4 xl:w-5 xl:h-5",
  style,
  showAltLink = false,
  altLinkClassName = "text-white/70 hover:text-white",
  noInstallingRedirect = false,
}: DownloadCtaProps) {
  const navigate = useNavigate();
  // 'mac' until proven otherwise — matches the prerendered markup, and a
  // wrong first frame is a working download link rather than a dead end.
  const [platform, setPlatform] = useState<VisitorPlatform>("mac");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p !== "mac") {
      // One event per mount so the variant split is measurable — this is the
      // denominator for "how many people saw the email/waitlist CTA".
      try {
        trackEvent("download_cta_variant", { placement, variant: p });
      } catch {
        /* analytics never breaks a CTA */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMacClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackDownloadClick(placement, e);
    // Feedback + next steps. The browser keeps the download running across the
    // SPA navigation; the label flip covers the gap so the click visibly did
    // something even before the route changes. Skip both for modified clicks
    // (cmd/middle-click opens a tab — the label lying "Downloading…" would be
    // wrong, and yanking the page they're still on would be worse).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    setDownloading(true);
    if (!noInstallingRedirect) {
      window.setTimeout(() => {
        navigate(`/desktop/installing?from=${encodeURIComponent(placement)}`);
      }, 900);
    } else {
      window.setTimeout(() => setDownloading(false), 4000);
    }
  };

  if (platform === "windows") {
    return (
      <div className="flex flex-col items-center gap-2.5">
        {/* Full-strength primary — the dimmed secondary treatment was for
            when it sat beside the mac button */}
        <WindowsWaitlistButton className={className} iconClassName={iconClassName} />
        {showAltLink && (
          <a
            href={MACOS_DOWNLOAD_URL}
            onClick={(e) => trackDownloadClick(placement, e)}
            className={`text-xs underline underline-offset-2 transition-colors ${altLinkClassName}`}
          >
            Have a Mac? Download for macOS instead
          </a>
        )}
      </div>
    );
  }

  if (platform === "ios" && IMESSAGE_HANDLE) {
    // iPhone — they text us, we reply with the link (MacLinkTextButton).
    // Email stays one tap away for anyone who'd rather not text.
    return (
      <div className="flex flex-col items-center gap-2.5">
        <MacLinkTextButton placement={placement} className={className} style={style} />
        {showAltLink && (
          <MacLinkEmailButton
            placement={placement}
            label="Or email it to me"
            className={`inline-flex items-center gap-1.5 text-xs underline underline-offset-2 transition-colors bg-transparent ${altLinkClassName}`}
            iconClassName="w-3 h-3"
          />
        )}
      </div>
    );
  }

  if (platform !== "mac") {
    // ios / android / linux / other — nothing installable here; email the link.
    return (
      <div className="flex flex-col items-center gap-2.5">
        <MacLinkEmailButton placement={placement} className={className} iconClassName={iconClassName} style={style} />
        {showAltLink && (
          <span className={`text-xs ${altLinkClassName}`}>
            Vovy is a Mac app (Apple Silicon). We'll send the link to open on your Mac.
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2.5">
      <a
        href={MACOS_DOWNLOAD_URL}
        onClick={onMacClick}
        style={style}
        className={className}
        aria-live="polite"
      >
        {downloading ? (
          <>
            <Check className={iconClassName} strokeWidth={3} />
            Downloading&hellip;
          </>
        ) : (
          <>
            <AppleLogo className={iconClassName} />
            Download for macOS
          </>
        )}
      </a>
      {showAltLink && (
        <WindowsWaitlistButton
          className={`inline-flex items-center gap-1.5 text-xs underline underline-offset-2 transition-colors bg-transparent ${altLinkClassName}`}
          iconClassName="w-3 h-3"
        />
      )}
    </div>
  );
}
